import React, { useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { Box, Divider, Grid, Typography } from '@material-ui/core';

import Container from '../../components/Container';
import Title from '../../components/Title';
import Subtitle from '../../components/Subtitle';
import Input from '../../components/Form/Input';
import Button from '../../components/Form/Button';
import TextHelper from '../../helpers/TextHelper';
import ValidateHelper from '../../helpers/ValidateHelper';
import useModal from '../../hooks/useModal';
import useSnackbar from '../../hooks/useSnackbar';
import EnderecoService from '../../services/EnderecoService';
import UsuarioService from '../../services/UsuarioService';

const cacheUfs = {
  /*[id_pais]: []*/
};
const cacheMunicipios = {
  /*[sg_ug]: []*/
};

export default function Perfil() {
  const formRef = useRef(null);

  const modal = useModal();
  const snackbar = useSnackbar();

  const [error, setError] = useState(false);
  const [dados, setDados] = useState(null);
  const [cepping, setCepping] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tiposTelefone, setTiposTelefone] = useState([]);
  const [paises, setPaises] = useState(null);
  const [ufs, setUfs] = useState(null);
  const [municipios, setMunicipios] = useState(null);

  const getTiposTelefone = async () => {
    setTiposTelefone([]);

    const newTiposTelefone = await UsuarioService.getTelefoneTipos().then(({ data }) => data);
    setTiposTelefone(newTiposTelefone);

    return newTiposTelefone;
  };

  const getPaises = async () => {
    setPaises([]);

    const newPaises = await EnderecoService.getPaises().then(({ data }) => data);
    setPaises(newPaises);

    return newPaises;
  };

  const getUfs = async (id_pais) => {
    let newUfs = cacheUfs[id_pais];
    setUfs(newUfs || []);

    if (id_pais && !newUfs) {
      newUfs = await EnderecoService.getUfs(id_pais).then(({ data }) => data);
      cacheUfs[id_pais] = newUfs;
      setUfs(newUfs);
    }

    return newUfs;
  };

  const getMunicipios = async (sg_uf) => {
    let newMunicipios = cacheMunicipios[sg_uf];
    setMunicipios(newMunicipios || []);

    if (sg_uf && !newMunicipios) {
      newMunicipios = await EnderecoService.getMunicipios(sg_uf).then(({ data }) => data);
      cacheMunicipios[sg_uf] = newMunicipios;
      setMunicipios(newMunicipios);
    }

    return newMunicipios;
  };

  // Preencher formulário ao buscar dados
  useEffect(() => {
    if (dados && formRef.current) {
      formRef.current.setData(dados);
    }
  }, [dados]);

  // componentDidMount
  useEffect(() => {
    (async function () {
      try {
        const newDados = await UsuarioService.getDadosPessoais().then(({ data }) => data);
        const { id_pais, sg_uf, nu_ddd, nu_telefone } = newDados;
        newDados.telefone = TextHelper.unmask(`${nu_ddd || ''}${nu_telefone || ''}`);

        await Promise.all([
          // Buscar dados de combos
          getTiposTelefone(),
          getPaises(),
          getUfs(id_pais),
          getMunicipios(sg_uf),
        ]);

        setDados(newDados);
      } catch (e) {
        setError(true);
      }
    })();
  }, []);

  const validate = (data) => {
    const errors = {};

    const { id_tipotelefone, telefone } = data;
    const phoneDigits = +id_tipotelefone === 3 ? 11 : 10;
    const phoneUnmasked = TextHelper.unmask(telefone);

    if (!id_tipotelefone) {
      errors.id_tipotelefone = 'Informe o tipo.';
    }

    if (!ValidateHelper.length(phoneUnmasked, phoneDigits, phoneDigits)) {
      errors.telefone = `Informe o DDD + ${phoneDigits - 2} dígitos.`;
    }

    if (!ValidateHelper.cep(data.st_cep)) {
      errors.st_cep = 'Informe o CEP.';
    }

    if (!ValidateHelper.length(data.st_endereco, 6)) {
      errors.st_endereco = 'Informe o endereço.';
    }

    if (data.nu_numero <= 0) {
      errors.nu_numero = 'Informe o número.';
    }

    if (!ValidateHelper.length(data.st_bairro, 3)) {
      errors.st_bairro = 'Informe o bairro.';
    }

    if (!data.sg_uf) {
      errors.sg_uf = 'Informe o UF.';
    }

    if (!data.id_municipio) {
      errors.id_municipio = 'Informe a cidade.';
    }

    return errors;
  };

  const handleChangeCep = async ({ target }) => {
    const st_cep = TextHelper.unmask(target.value);

    if (st_cep.length === 8) {
      setCepping(true);

      const { data: endereco } = (await EnderecoService.getByCep(st_cep).catch(() => null)) || {};

      if (!endereco) {
        modal.alert({
          title: 'Atenção',
          content: `O CEP ${target.value} não foi encontrado.`,
        });
      }

      const newDados = {
        ...dados,
        ...(endereco || {}),
      };

      await getMunicipios(newDados?.sg_uf);

      setDados(newDados);
      setCepping(false);
    }
  };

  const handleChangePais = async ({ target }) => {
    formRef.current.setFieldValue('sg_uf', '');
    await getUfs(`${target.value}`);
  };

  const handleChangeUf = async ({ target }) => {
    formRef.current.setFieldValue('id_municipio', '');
    await getMunicipios(`${target.value}`);
  };

  const handleSubmit = async (newData) => {
    const data = {
      ...dados,
      ...newData,
      bl_padrao: 1,
    };

    // Validação
    const errors = validate(data);
    formRef.current.setErrors(errors);

    if (Object.values(errors).length) {
      return modal.alert({
        title: 'Verifique os campos',
        content: ValidateHelper.listErrors(errors),
      });
    }

    setSaving(true);

    try {
      await EnderecoService.saveEnderecoTelefone(data);
      snackbar.success('Os seus dados foram atualizados!');
    } catch (e) {
      snackbar.error('Tivemos um problema para salvar os dados.');
    }

    setSaving(false);
  };

  return (
    <Container error={error && 'Encontramos um problema ao exibir os dados. Tente novamente em alguns instantes.'}>
      <Title>seus dados</Title>

      <Typography>Mantenha os seus dados cadastrais sempre atualizados.</Typography>

      <Form ref={formRef} onSubmit={handleSubmit}>
        <Grid container justifyContent="space-between">
          <Grid item xs={12} md={5}>
            <Grid container justifyContent="space-between">
              <Grid item xs={12}>
                <Subtitle mb={2}>dados cadastrais</Subtitle>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Input label="Nome" name="st_nomecompleto" autoComplete="name" loading={!dados} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Input label="Data de Nascimento" name="dt_nascimento" mask="date" loading={!dados} disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Input label="CPF" name="st_cpf" mask="cpf" loading={!dados} disabled />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Subtitle mb={2}>dados para contato</Subtitle>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Input type="email" label="E-mail" name="st_email" autoComplete="email" loading={!dados} disabled />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Input
                      label="Tipo"
                      name="id_tipotelefone"
                      loading={!dados}
                      options={tiposTelefone.map((tipo) => ({
                        value: tipo.id_tipotelefone,
                        label: tipo.st_tipotelefone,
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Input label="Telefone" name="telefone" autoComplete="tel-national" mask="phone" loading={!dados} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={5}>
            <Subtitle mb={2}>seu endereço</Subtitle>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Input label="CEP" name="st_cep" mask="cep" numeric autoComplete="postal-code" loading={!dados} onChange={handleChangeCep} />
              </Grid>
              <Grid item xs={12}>
                <Input label="Endereço" name="st_endereco" autoComplete="address-line1" loading={!dados || cepping} />
              </Grid>
              <Grid item xs={5} md={4}>
                <Input type="number" label="Nº." name="nu_numero" loading={!dados} />
              </Grid>
              <Grid item xs={7} md={8}>
                <Input label="Complemento" name="st_complemento" autoComplete="address-line3" loading={!dados} />
              </Grid>
              <Grid item xs={12}>
                <Input label="Bairro" name="st_bairro" autoComplete="address-line2" loading={!dados || cepping} />
              </Grid>
              <Grid item xs={12} hidden>
                <Input
                  disabled
                  label="País"
                  name="id_pais"
                  loading={!dados || !paises || cepping}
                  value="22" // Brasil
                  defaultValue="22" // Brasil
                  onChange={handleChangePais}
                  options={paises?.map((item) => ({
                    value: item.id_pais,
                    label: item.st_nomepais,
                  }))}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Input
                  label="UF"
                  name="sg_uf"
                  loading={!dados || !ufs || cepping}
                  onChange={handleChangeUf}
                  options={ufs?.map((item) => ({
                    value: item.sg_uf,
                    label: item.st_uf,
                  }))}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Input
                  label="Cidade"
                  name="id_municipio"
                  loading={!dados || !municipios || cepping}
                  options={municipios?.map((item) => ({
                    value: item.id_municipio,
                    label: item.st_nomemunicipio,
                  }))}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item container spacing={3} justifyContent="flex-end" alignItems="center">
            <Grid item xs>
              <Divider />
            </Grid>
            <Grid item>
              <Box my={3}>
                <Button type="submit" loading={saving} disabled={!dados || cepping}>
                  salvar dados
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Form>
    </Container>
  );
}
